import { useEffect, useRef, useState } from 'react';

interface Attributed {
  id: string;
  updatedBy: string;
  updatedAt: string;
}

/**
 * Tracks which item ids just changed *because of someone else* (DESIGN-BRIEF §5 "subtle
 * someone-else-just-changed-this affordance"; ROADMAP §2 Phase 1 Lists item 4). Compares
 * `updatedAt` against the previous render per id:
 *  - The very first snapshot only seeds the baseline — nothing flashes on initial load.
 *  - A later change is flagged only when `updatedBy !== meId`, so local edits (which are
 *    already applied by the time this effect runs) never flash.
 */
export function useRemoteChangeFlash<T extends Attributed>(
  items: T[],
  meId: string | null,
  durationMs = 2400,
): Set<string> {
  const seen = useRef<Map<string, string>>(new Map());
  const mounted = useRef(false);
  const [flashing, setFlashing] = useState<Set<string>>(new Set());

  useEffect(() => {
    const next = new Set<string>();
    for (const item of items) {
      const prev = seen.current.get(item.id);
      if (
        mounted.current &&
        prev !== undefined &&
        prev !== item.updatedAt &&
        item.updatedBy !== meId
      ) {
        next.add(item.id);
      }
      seen.current.set(item.id, item.updatedAt);
    }
    mounted.current = true;
    if (next.size > 0) {
      setFlashing((current) => new Set([...current, ...next]));
      const timers = [...next].map((id) =>
        setTimeout(() => {
          setFlashing((current) => {
            const copy = new Set(current);
            copy.delete(id);
            return copy;
          });
        }, durationMs),
      );
      return () => timers.forEach(clearTimeout);
    }
    return undefined;
    // `items` is a fresh array each render from the sync hooks; comparing by content via
    // the `seen` ref (not identity) is the point, so it's deliberately the only dependency
    // (there is no react-hooks eslint plugin in this repo to appease either way).
  }, [items]);

  return flashing;
}
